import './NotificationLink.scss';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { ExportDataFilter } from '../../projects/projects.model';
import { NotificationsHub } from '../components/NotificationsHub';
import { toNotification } from '../mappers/hubNotification.mapper';
import { IBaseNotification, IHubNotification, ILocalizable, NotificationLevel, NotificationPopupType, NotificationType } from '../models/notification.model';
import { centerTypeSelector } from '../services/notification.helpers';
import { INotificationsService } from '../services/notifications.service';

export interface INotificationModel extends INotificationMapper {
  id: string;
  type: NotificationPopupType;
  date: Date;
  projectExportId?: string;
  exportDataFilter?: ExportDataFilter;
  onRemove(id: string): void;
  workspaceId?: string;
  jobType?: string;
  jobId?: string;
  notificationType?: NotificationType.JobFinished | NotificationType.JobFailed | NotificationType.JobRetrying | NotificationType.JobLongRunTime;
}

export interface INotificationMapper {
  level: NotificationLevel;
  title: string | ILocalizable;
  body: string | ILocalizable | ReactNode;
  wasRead: boolean;
}

export const NotificationsHubContainer = observer((): ReactElement => {
  const notificationService = useInjection(INotificationsService.$);

  const [showNotifications, setShowNotifications] = useState<boolean>(false);

  useEffect(() => {
    void notificationService.initializeHubAsync();
  }, []);

  const handleToggle = async (): Promise<void> => {
    if (!showNotifications) {
      await notificationService.markAsRead();
    }
    setShowNotifications((prev) => !prev);
  };

  const notifications = notificationService.notificationStore.notifications
    .filter(centerTypeSelector)
    .sort((a: IBaseNotification, b: IBaseNotification) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    })
    .map((n) => ({
      ...toNotification(n as IHubNotification),
      id: n.id,
      date: new Date(n.date),
      type: n.popupType,
      wasRead: n.wasRead,
      onRemove: async (): Promise<void> => {
        await notificationService.remove(n);
      },
    }));

  const handleClearAll = async (): Promise<void> => {
    await notificationService.clearHub();
  };

  return (
    <NotificationsHub
      notifications={notifications}
      onToggle={handleToggle}
      onClearAll={handleClearAll}
    />
  );
});
